export type Interceptor = (
  request: Request,
  fetch: typeof self.fetch,
) => Promise<Response> | undefined

const interceptors = new Set<Interceptor>()

const originalFetch = self.fetch
const interceptingFetch = (...args: Parameters<typeof originalFetch>) => {
  const request = new Request(...args)

  for (const interceptor of interceptors) {
    const response = interceptor(request, originalFetch)
    if (response) return response
  }

  return originalFetch(request)
}

const setup = (interceptor: Interceptor) => {
  // rewrite the global `fetch` with a custom implementation intercepting all requests
  if (self.fetch !== interceptingFetch) self.fetch = interceptingFetch
  interceptors.add(interceptor)
}

const teardown = (interceptor: Interceptor) => {
  interceptors.delete(interceptor)
  // if there are no more interceptors, restore the original `fetch`
  if (interceptors.size === 0) self.fetch = originalFetch
}

/**
 * Intercepts all global `fetch` requests and calls the `interceptor` function with the intercepted request as parameter.
 *
 * @param interceptor - a function to be called with the intercepted request as parameter.
 * The function should either return `undefined` or a `Response` instance to be used instead of the original response.
 */
export const interceptFetchRequest = (interceptor: Interceptor) => {
  setup(interceptor)

  return {
    terminate: () => teardown(interceptor),
  }
}
